// src/services/supabase/onboarding-commit.service.ts
import { supabase } from '@/lib/supabase'
import { shopService } from '@/services/shop.service'
import { newHouseholdCohortFields } from '@/services/businessCohort'
import { todayStr } from '@/services/date.service'
import type { OnboardingDraft, CommitResult, OnboardingDraftTask, OnboardingDraftReward } from '@/types/onboarding'

function birthDateFromYear(year: number): string | null {
  if (!year || Number.isNaN(year)) return null
  // 1er janvier de l'année de naissance (cohérent avec l'usage onboarding actuel).
  return `${year}-01-01`
}

function taskPayload(t: OnboardingDraftTask) {
  const specific = Array.isArray(t.assignedChildNames) && t.assignedChildNames.length > 0
  return {
    title: t.title,
    description: t.description ?? '',
    points: t.points ?? 1,
    frequency: t.frequency ?? 'daily',
    assignment: specific ? 'specific' : 'all',
    child_names: specific ? t.assignedChildNames : [],
    // Lu par commit_onboarding_draft depuis la 3.0.2 ; ignoré avant.
    template_id: t.templateId ?? null,
  }
}

function rewardPayload(r: OnboardingDraftReward) {
  const specific = Array.isArray(r.assignedChildNames) && r.assignedChildNames.length > 0
  return {
    title: r.title,
    description: r.description ?? '',
    required_points: r.requiredPoints,
    category: r.category,
    icon: r.icon ?? null,
    is_recurring: r.isRecurring ?? false,
    child_names: specific ? r.assignedChildNames : [],
    template_id: r.templateId ?? null,
  }
}

export const onboardingCommitService = {
  async commitDraft(draft: OnboardingDraft): Promise<CommitResult> {
    const payload = {
      commit_id: draft.commitId,
      parent_name: draft.parentName,
      // Le rôle déclaré au premier écran. Absent des brouillons d'avant ce
      // champ : le RPC écrit alors null, et le commit aboutit quand même.
      parent_type: draft.parentType ?? null,
      // « Combien d'enfants ? » (HARMONIA-94). Lu par le RPC depuis la 3.0.4,
      // qui ignore toute valeur hors 1 à 4.
      declared_children_count: draft.declaredChildrenCount ?? null,
      // Le jour LOCAL du parent, d'où partent les tâches (HARMONIA-99). La base
      // est en UTC : sans lui, une inscription le soir aux Amériques donnait des
      // tâches qui démarraient le lendemain. Le RPC ne le croit qu'à un jour près.
      today: todayStr(),
      locale: draft.locale,
      household_name: draft.parentName ? `Famille de ${draft.parentName}` : 'Ma famille',
      // Le foyer que CETTE app crée relève du modèle par profil, et il le dit
      // lui-même. Une version antérieure n'envoie pas ces champs et crée donc
      // un foyer `legacy`, qu'elle sait faire fonctionner : c'est ce qui permet
      // de déployer la base sans attendre l'approbation store.
      ...newHouseholdCohortFields(),
      children: draft.children.map((c) => ({
        name: c.name,
        gender: c.gender,
        birth_date: birthDateFromYear(c.birthYear),
        avatar: c.avatarAnimalId ?? null,
        color: c.color ?? null,
      })),
      tasks: draft.tasks.map(taskPayload),
      rewards: draft.rewards.map(rewardPayload),
    }

    const { data, error } = await (supabase.rpc as any)('commit_onboarding_draft', { p_payload: payload })
    if (error) throw new Error(error.message)

    const result: CommitResult = {
      householdId: (data as any).household_id,
      childrenIds: (data as any).children_ids ?? [],
      status: (data as any).status,
    }

    // Effets cosmétiques best-effort : starter items boutique par enfant.
    // Un échec ici NE doit PAS faire échouer le commit (self-healing ultérieur possible).
    // En parallèle : ces octrois sont indépendants entre enfants, et les
    // enchaîner ajoutait un aller-retour par enfant tout au bout de la création
    // de compte, l'étape la plus coûteuse du parcours. `allSettled` conserve le
    // caractère best-effort : un échec n'en annule aucun autre.
    await Promise.allSettled(
      result.childrenIds.map(childId =>
        shopService.grantFreeStarterItems(childId).catch((e: unknown) => {
          console.warn('[commitDraft] grantFreeStarterItems failed (best-effort):', childId, e)
        }),
      ),
    )

    return result
  },
}
